import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useCallback, useEffect, useState } from 'react';
import { FiCamera, FiCheck, FiUpload } from 'react-icons/fi';
import { Footer, SEO } from '@/components';
import { DivorceAccountGate } from '@/components/divorce/DivorceAccountGate';
import { track } from '@/lib/analytics';
import { apiFetch } from '@/lib/api';
import { requireDivorceProduct } from '@/lib/divorce-gate';
import {
  DISCLOSURE_GRANULARITY,
  DISCLOSURE_VALUATION,
  DOC_PREP_DISCLAIMER,
  LLC_DISPOSITION_SHAPES,
  MISSED_DISCLOSURE_DEADLINE,
  REJECTION_AND_REVISION,
  SERVER_INSTRUCTIONS,
} from '@/lib/divorce-legal';

export const Route = createFileRoute('/divorce/case')({
  beforeLoad: async () => {
    await requireDivorceProduct();
  },
  validateSearch: (search: Record<string, unknown>) => ({
    id: typeof search.id === 'string' ? search.id : undefined,
    invite: typeof search.invite === 'string' ? search.invite : undefined,
    claim: typeof search.claim === 'string' ? search.claim : undefined,
  }),
  component: DivorceCasePage,
});

const nunito = {
  fontFamily:
    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
};

type Deadline = {
  key: string;
  label: string;
  due_on: string;
  days_left: number;
  severity: string;
  copy: string;
};

type CasePayload = {
  public_id: string;
  stage: string;
  case_number: string | null;
  county: string | null;
  filed_on: string | null;
  final_on: string | null;
  answers: Record<string, any>;
  seats: Array<{
    id: number;
    role: string;
    email: string | null;
    claimed: boolean;
    fl141_attested: boolean;
    fl141_filed_on: string | null;
    disclosure_status: string;
    disclosure_answers?: { fl150?: Record<string, any> };
  }>;
  inventory: Array<Record<string, any>>;
  accounts: Array<Record<string, any>>;
  timeline: {
    missed_disclosure: boolean;
    earliest_final_on: string | null;
    disclosure_due_on: string | null;
    deadlines: Deadline[];
  };
  form_watches: Array<{ form_number: string; message: string }>;
  consistency: { ok: boolean; issues: Array<{ code: string; message: string; blocking: boolean }> };
  room_walk_enabled: boolean;
  viewer_seat_id: number | null;
  owner_claim_token?: string;
};

function DivorceCasePage() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [caseData, setCaseData] = useState<CasePayload | null>(null);
  const [door, setDoor] = useState('filed');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [spouseEmail, setSpouseEmail] = useState('');
  const [serverEmail, setServerEmail] = useState('');
  const [spouseInvite, setSpouseInvite] = useState<{
    message: string;
    url?: string;
  } | null>(null);
  const [serverInvite, setServerInvite] = useState<{
    message: string;
    url?: string;
  } | null>(null);
  const [fl150, setFl150] = useState({
    employer: '',
    occupation: '',
    business_name: '',
    self_employment_income_cents: '',
  });
  const [redactFile, setRedactFile] = useState<File | null>(null);
  const [finalDate, setFinalDate] = useState('');
  const [inviteClaimed, setInviteClaimed] = useState(false);
  const [stamp, setStamp] = useState({
    case_number: '',
    county: '',
    filed_on: '',
    petitioner_name: '',
    respondent_name: '',
  });

  const load = useCallback(
    async (publicId: string) => {
      const seatToken = search.invite || search.claim;
      const query = seatToken ? `?seat_token=${encodeURIComponent(seatToken)}` : '';
      const resp = await apiFetch(getToken, `/v1/divorce/cases/${publicId}${query}`);
      if (!resp.ok) throw new Error('Could not load case');
      const data = (await resp.json()) as CasePayload;
      setCaseData(data);
      if (data.final_on) setFinalDate(data.final_on);
      setStamp({
        case_number: data.case_number || '',
        county: data.county || '',
        filed_on: data.filed_on || '',
        petitioner_name: String(data.answers?.petitioner?.name || ''),
        respondent_name: String(data.answers?.respondent?.name || ''),
      });
      const mine = data.seats.find((seat) => seat.disclosure_answers?.fl150);
      if (mine?.disclosure_answers?.fl150) {
        const row = mine.disclosure_answers.fl150;
        setFl150({
          employer: row.employer || '',
          occupation: row.occupation || '',
          business_name: row.business_name || '',
          self_employment_income_cents:
            row.self_employment_income_cents != null ? String(row.self_employment_income_cents) : '',
        });
      }
      track('Divorce Case Screen', { stage: data.stage, days_to_deadline: data.timeline.deadlines[0]?.days_left });
    },
    [getToken, search.claim, search.invite],
  );

  useEffect(() => {
    if (search.id) void load(search.id).catch((err) => setError(String(err)));
  }, [search.id, load]);

  const startCase = async () => {
    setBusy(true);
    setError('');
    try {
      const resp = await apiFetch(getToken, '/v1/divorce/cases', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ intake_door: door, jurisdiction_code: 'CA', petition_kind: 'joint' }),
      });
      if (!resp.ok) throw new Error('Could not start case');
      const data = (await resp.json()) as CasePayload;
      setCaseData(data);
      track('Divorce Case Started', { door });
      await navigate({
        to: '/divorce/case',
        search: {
          id: data.public_id,
          invite: undefined,
          claim: data.owner_claim_token,
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start');
    } finally {
      setBusy(false);
    }
  };

  const confirmStamp = async () => {
    if (!caseData) return;
    setBusy(true);
    try {
      const resp = await apiFetch(getToken, `/v1/divorce/cases/${caseData.public_id}/stamp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          case_number: stamp.case_number || undefined,
          county: stamp.county || undefined,
          filed_on: stamp.filed_on || undefined,
          petitioner_name: stamp.petitioner_name || undefined,
          respondent_name: stamp.respondent_name || undefined,
        }),
      });
      if (!resp.ok) throw new Error('Could not save stamp');
      setCaseData((await resp.json()) as CasePayload);
      track('Stamped Petition Confirmed');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  const onFile = async (file: File, expectedKind: string) => {
    if (!caseData) return;
    const base64 = await fileToJpegBase64(file);
    track('Stamped Petition Uploaded', { kind: expectedKind });
    const resp = await apiFetch(getToken, `/v1/divorce/cases/${caseData.public_id}/extract`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ image_base64: base64, content_type: 'image/jpeg', expected_kind: expectedKind }),
    });
    const result = await resp.json();
    if (!result.matched) {
      setError(result.error === 'WRONG_DOCUMENT' ? 'That does not look like the expected form.' : 'Could not read that photo.');
      return result;
    }
    const next = { ...stamp };
    for (const proposal of result.proposals || []) {
      if (proposal.answer_path === 'case.number') next.case_number = String(proposal.value);
      if (proposal.answer_path === 'residency.county') next.county = String(proposal.value);
      if (proposal.answer_path === 'case.filed_at') next.filed_on = String(proposal.value);
      if (proposal.answer_path === 'petitioner.name') next.petitioner_name = String(proposal.value);
      if (proposal.answer_path === 'respondent.name') next.respondent_name = String(proposal.value);
    }
    setStamp(next);
    return result;
  };

  const ingestStatement = async (file: File) => {
    if (!caseData) return;
    const b64 = await fileToBase64(file);
    const resp = await apiFetch(getToken, `/v1/divorce/cases/${caseData.public_id}/financial-documents`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename: file.name, content_base64: b64 }),
    });
    if (!resp.ok) {
      setError('Could not read that statement');
      return;
    }
    track('Financial Statement Uploaded', { filename: file.name });
    await load(caseData.public_id);
  };

  const confirmAccount = async (accountId: number) => {
    if (!caseData) return;
    await apiFetch(getToken, `/v1/divorce/cases/${caseData.public_id}/accounts/confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ account_id: accountId }),
    });
    track('Financial Account Confirmed');
    await load(caseData.public_id);
  };

  const alreadyDivided = async () => {
    if (!caseData) return;
    await apiFetch(getToken, `/v1/divorce/cases/${caseData.public_id}/inventory`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fl142_item: 2,
        description: 'Household furniture, furnishings, appliances — already divided',
        already_divided: true,
        confirmed: true,
        disposition: 'already_divided',
      }),
    });
    track('Furnishings Marked Already Divided');
    await load(caseData.public_id);
  };

  const downloadCaseDocument = async (endpoint: string, filename: string) => {
    if (!caseData) return;
    const response = await apiFetch(
      getToken,
      `/v1/divorce/cases/${caseData.public_id}/${endpoint}`,
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      setError(body.detail || 'That document is not available yet.');
      return;
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  if (!caseData) {
    return (
      <div className='min-h-screen bg-white' style={nunito}>
        <SEO
          title='Start your Harbor Divorce case'
          description='Start where you are in a California joint-petition divorce and keep every stage in one place.'
          path='/divorce/case'
        />
        <main className='mx-auto max-w-xl px-4 py-12'>
          <h1 className='text-3xl font-extrabold text-gray-900'>Where are you in the case?</h1>
          <p className='mt-2 text-gray-600'>Start where you are. You can snap a stamped petition instead of redoing it.</p>
          <div className='mt-6 space-y-2'>
            {[
              ['not_filed', 'We have not filed yet'],
              ['filed', 'We already filed (FL-700)'],
              ['disclosures_exchanged', 'We already exchanged disclosures'],
              ['have_agreement', 'We have a signed agreement'],
              ['judgment_submitted', 'Judgment is with the court'],
            ].map(([value, label]) => (
              <label key={value} className='flex items-center gap-3 rounded-xl border border-gray-200 px-4 py-3'>
                <input type='radio' name='door' checked={door === value} onChange={() => setDoor(value)} />
                {label}
              </label>
            ))}
          </div>
          <button
            type='button'
            onClick={() => void startCase()}
            disabled={busy}
            className='mt-6 w-full rounded-xl bg-primary px-4 py-3 font-semibold text-white disabled:opacity-50'
          >
            {busy ? 'Starting…' : 'Continue'}
          </button>
          {error ? <p className='mt-3 text-sm text-red-600'>{error}</p> : null}
          <p className='mt-8 text-xs text-gray-500'>{DOC_PREP_DISCLAIMER}</p>
        </main>
        <Footer />
      </div>
    );
  }

  const hero = caseData.timeline.deadlines.find((d) => d.key === 'disclosure_due') || caseData.timeline.deadlines[0];

  return (
    <div className='min-h-screen bg-white' style={nunito}>
      <SEO
        title='Your Harbor Divorce case'
        description='Track deadlines, disclosures, service, and judgment forms for your California joint-petition divorce.'
        path='/divorce/case'
      />
      <main className='mx-auto max-w-2xl px-4 py-10 space-y-8'>
        <header>
          <p className='text-sm font-semibold uppercase tracking-wide text-primary'>Harbor Divorce</p>
          <h1 className='mt-1 text-3xl font-extrabold text-gray-900'>Your case</h1>
          <p className='mt-1 text-gray-600'>
            {caseData.case_number ? `Case ${caseData.case_number}` : 'Add the clerk stamp to unlock dates'}
            {caseData.county ? ` · ${caseData.county}` : ''}
          </p>
        </header>

        {search.invite || search.claim ? (
          <section className='rounded-2xl border border-primary/30 p-5'>
            <h2 className='text-xl font-bold text-gray-900'>Your private seat</h2>
            <p className='mt-1 text-sm text-gray-600'>
              Verify the invited email before opening your private income and expense
              answers.
            </p>
            <DivorceAccountGate
              claimToken={(search.invite || search.claim) as string}
              ready={inviteClaimed}
              claimEndpoint={`/v1/divorce/cases/${caseData.public_id}/claim-seat`}
              claimTokenField='invite_token'
              title={search.invite ? 'Claim your spouse seat' : 'Save your case to your email'}
              description={
                search.invite
                  ? 'Harbor gives each spouse a separate email login. Shared inventory is visible to both; your FL-150 stays private until you serve it.'
                  : 'Verify your email so you can return securely. Your FL-150 stays private to your seat until you serve it.'
              }
              successMessage='Your private seat is ready'
              onReady={() => {
                setInviteClaimed(true);
                track('Divorce Spouse Seat Claimed');
                void load(caseData.public_id);
                void navigate({
                  to: '/divorce/case',
                  search: {
                    id: caseData.public_id,
                    invite: undefined,
                    claim: undefined,
                  },
                });
              }}
            />
          </section>
        ) : null}

        {!caseData.viewer_seat_id ? (
          <p className='rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900'>
            Shared case preview. Verify your email above before making changes or
            downloading documents.
          </p>
        ) : null}

        <div
          className={
            caseData.viewer_seat_id
              ? 'space-y-8'
              : 'pointer-events-none space-y-8 opacity-75'
          }
        >
        {caseData.stage !== 'final' && hero ? (
          <section className='rounded-2xl bg-primary/10 p-5'>
            <p className='text-sm font-semibold text-primary'>{hero.label}</p>
            <p className='mt-1 text-4xl font-extrabold text-gray-900'>
              {hero.days_left < 0 ? `${Math.abs(hero.days_left)} days past` : `${hero.days_left} days left`}
            </p>
            <p className='mt-2 text-sm text-gray-700'>
              {caseData.timeline.missed_disclosure ? MISSED_DISCLOSURE_DEADLINE : hero.copy}
            </p>
          </section>
        ) : null}

        {caseData.stage === 'final' ? (
          <section className='rounded-2xl bg-emerald-50 p-5 text-emerald-950'>
            <p className='text-sm font-semibold uppercase tracking-wide'>Case complete</p>
            <h2 className='mt-1 text-2xl font-extrabold'>Your divorce is marked final</h2>
            <p className='mt-2 text-sm'>
              Notice of Entry of Judgment received{caseData.final_on ? ` on ${caseData.final_on}` : ''}.
              Your evidence is scheduled for deletion after the retention window.
            </p>
            <a href='/app' className='mt-3 inline-block font-semibold text-primary'>
              Continue to Harbor co-parenting
            </a>
          </section>
        ) : null}

        <section className='space-y-3 rounded-2xl border border-gray-200 p-5'>
          <h2 className='text-xl font-bold'>Snap the stamped petition</h2>
          <p className='text-sm text-gray-600'>Photo or upload. Confirm what Harbor reads before anything is saved.</p>
          <div className='flex gap-3'>
            <label className='flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl border border-primary px-3 py-3 font-semibold text-primary'>
              <FiCamera /> Take photo
              <input
                type='file'
                accept='image/*'
                capture='environment'
                className='hidden'
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void onFile(file, 'filed_petition_fl700');
                }}
              />
            </label>
            <label className='flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl border border-gray-300 px-3 py-3 font-semibold text-gray-800'>
              <FiUpload /> Upload
              <input
                type='file'
                accept='image/*'
                className='hidden'
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void onFile(file, 'filed_petition_fl700');
                }}
              />
            </label>
          </div>
          <div className='grid gap-2 sm:grid-cols-2'>
            <input className='rounded-lg border px-3 py-2' placeholder='Case number' value={stamp.case_number} onChange={(e) => setStamp({ ...stamp, case_number: e.target.value })} />
            <input className='rounded-lg border px-3 py-2' placeholder='County' value={stamp.county} onChange={(e) => setStamp({ ...stamp, county: e.target.value })} />
            <input className='rounded-lg border px-3 py-2' type='date' value={stamp.filed_on} onChange={(e) => setStamp({ ...stamp, filed_on: e.target.value })} />
            <input className='rounded-lg border px-3 py-2' placeholder='Your name' value={stamp.petitioner_name} onChange={(e) => setStamp({ ...stamp, petitioner_name: e.target.value })} />
            <input className='rounded-lg border px-3 py-2' placeholder="Spouse's name" value={stamp.respondent_name} onChange={(e) => setStamp({ ...stamp, respondent_name: e.target.value })} />
          </div>
          <button type='button' onClick={() => void confirmStamp()} className='rounded-xl bg-primary px-4 py-2 font-semibold text-white'>
            Confirm facts of record
          </button>
        </section>

        <section className='space-y-3 rounded-2xl border border-gray-200 p-5'>
          <h2 className='text-xl font-bold'>Drop in statements</h2>
          <p className='text-sm text-gray-600'>
            E*TRADE exports, credit-union statements, Chase cards — PDF, CSV, XLSX, or OFX. Harbor sorts them into the
            court&apos;s boxes. You confirm each account.
          </p>
          <p className='text-sm text-gray-600'>{DISCLOSURE_VALUATION}</p>
          <label className='flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-primary px-3 py-3 font-semibold text-primary'>
            <FiUpload /> Upload statement or export
            <input
              type='file'
              accept='image/*,.csv,.tsv,.ofx,.qfx,.pdf,.xlsx,text/csv,application/pdf'
              className='hidden'
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (!file) return;
                if (file.type.startsWith('image/')) {
                  setRedactFile(file);
                  return;
                }
                void ingestStatement(file);
              }}
            />
          </label>
          {caseData.accounts.map((account) => (
            <div key={account.id} className='flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2'>
              <div>
                <p className='font-semibold'>
                  {account.institution}
                  {account.last4
                    ? ` ending ${account.last4}`
                    : account.account_type
                      ? ` ${String(account.account_type).replace(/_/g, ' ')}`
                      : ''}
                </p>
                <p className='text-sm text-gray-600'>
                  FL-142 item {account.fl142_item || '—'} · {account.status}
                </p>
              </div>
              {account.status !== 'confirmed' ? (
                <button type='button' onClick={() => void confirmAccount(account.id)} className='font-semibold text-primary'>
                  Confirm
                </button>
              ) : (
                <FiCheck className='text-primary' />
              )}
            </div>
          ))}
          <p className='text-sm text-gray-600'>{DISCLOSURE_GRANULARITY}</p>
          <button type='button' onClick={() => void alreadyDivided()} className='text-sm font-semibold text-primary'>
            Household items are already divided
          </button>
          {caseData.room_walk_enabled ? (
            <p className='text-sm text-gray-500'>Room-walk photos are on for this case (descriptions first; you pick a value range).</p>
          ) : null}
        </section>

        <section className='space-y-3 rounded-2xl border border-gray-200 p-5'>
          <h2 className='text-xl font-bold'>Shared inventory</h2>
          {caseData.inventory.length === 0 ? (
            <p className='text-sm text-gray-600'>Nothing confirmed yet.</p>
          ) : (
            <ul className='space-y-2'>
              {caseData.inventory.map((item) => (
                <li key={item.id} className='flex items-center gap-3 rounded-lg bg-gray-50 px-3 py-2 text-sm'>
                  <span className='min-w-0 flex-1'>
                    <span className='font-semibold'>Item {item.fl142_item}</span> — {item.description}
                  </span>
                  <select
                    aria-label={`Disposition for ${item.description}`}
                    className='rounded-lg border border-gray-300 bg-white px-2 py-1'
                    value={item.disposition || 'undecided'}
                    onChange={async (event) => {
                      const disposition = event.target.value;
                      await apiFetch(
                        getToken,
                        `/v1/divorce/cases/${caseData.public_id}/inventory/${item.id}`,
                        {
                          method: 'PATCH',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ disposition }),
                        },
                      );
                      track('Inventory Disposition Changed', {
                        fl142_item: item.fl142_item,
                        disposition,
                      });
                      await load(caseData.public_id);
                    }}
                  >
                    <option value='undecided'>Decide later</option>
                    <option value='petitioner'>Petitioner keeps</option>
                    <option value='respondent'>Respondent keeps</option>
                    <option value='sell'>Sell / pay off</option>
                    <option value='already_divided'>Already divided</option>
                  </select>
                </li>
              ))}
            </ul>
          )}
          <p className='text-sm text-gray-600'>{LLC_DISPOSITION_SHAPES}</p>
        </section>

        <section className='space-y-3 rounded-2xl border border-gray-200 p-5'>
          <h2 className='text-xl font-bold'>Invite and serve</h2>
          <p className='text-sm text-gray-600'>{SERVER_INSTRUCTIONS}</p>
          <div className='flex gap-2'>
            <input className='flex-1 rounded-lg border px-3 py-2' placeholder="Spouse's email" value={spouseEmail} onChange={(e) => setSpouseEmail(e.target.value)} />
            <button
              type='button'
              className='rounded-xl bg-primary px-4 py-2 font-semibold text-white'
              onClick={async () => {
                const response = await apiFetch(getToken, `/v1/divorce/cases/${caseData.public_id}/invite-spouse`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ email: spouseEmail }),
                });
                const result = await response.json();
                if (!response.ok) {
                  setSpouseInvite({ message: result.detail || 'Could not create invite.' });
                  return;
                }
                setSpouseInvite({
                  message: result.email_sent ? 'Invite email sent.' : 'Invite created. Copy the link to share it.',
                  url: result.invite_url,
                });
                track('Spouse Invite Sent');
              }}
            >
              Invite
            </button>
          </div>
          {spouseInvite ? (
            <InviteResult result={spouseInvite} label='spouse invite' />
          ) : null}
          <div className='flex gap-2'>
            <input className='flex-1 rounded-lg border px-3 py-2' placeholder="Server's email" value={serverEmail} onChange={(e) => setServerEmail(e.target.value)} />
            <button
              type='button'
              className='rounded-xl border border-primary px-4 py-2 font-semibold text-primary'
              onClick={async () => {
                const response = await apiFetch(getToken, `/v1/divorce/cases/${caseData.public_id}/server-invite`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ email: serverEmail }),
                });
                const result = await response.json();
                if (!response.ok) {
                  setServerInvite({ message: result.detail || 'Could not create invite.' });
                  return;
                }
                setServerInvite({
                  message: result.email_sent ? 'Server instructions sent.' : 'Invite created. Copy the link to share it.',
                  url: result.invite_url,
                });
                track('Server Invite Sent');
              }}
            >
              Invite server
            </button>
          </div>
          {serverInvite ? (
            <InviteResult result={serverInvite} label='server invite' />
          ) : null}
          <div className='flex flex-wrap gap-3'>
            <button
              type='button'
              className='font-semibold text-primary'
              onClick={() =>
                void downloadCaseDocument(
                  'disclosure-packet',
                  'draft-disclosure-workbook.pdf',
                )
              }
            >
              Download draft disclosure workbook
            </button>
            <button
              type='button'
              className='font-semibold text-primary'
              onClick={() =>
                void downloadCaseDocument('extension-letter', 'disclosure-extension.pdf')
              }
            >
              Download extension letter
            </button>
            <button
              type='button'
              className='font-semibold text-primary'
              onClick={() =>
                void downloadCaseDocument(
                  'judgment-packet',
                  'draft-judgment-checklist.pdf',
                )
              }
            >
              Download draft judgment checklist
            </button>
            <button
              type='button'
              className='font-semibold text-primary'
              onClick={() => void downloadCaseDocument('export', 'harbor-divorce-export.zip')}
            >
              Export bundle
            </button>
          </div>
          {!caseData.consistency.ok ? (
            <ul className='text-sm text-amber-800'>
              {caseData.consistency.issues.map((issue) => (
                <li key={issue.code}>{issue.message}</li>
              ))}
            </ul>
          ) : null}
        </section>

        {caseData.viewer_seat_id ? (
        <section className='space-y-3 rounded-2xl border border-gray-200 p-5'>
          <h2 className='text-xl font-bold'>Your FL-150 (private to you)</h2>
          <p className='text-sm text-gray-600'>
            Income and expense answers stay on your seat until you serve. Section 7 is for self-employment or an LLC draw.
          </p>
          <div className='grid gap-2 sm:grid-cols-2'>
            <input className='rounded-lg border px-3 py-2' placeholder='Employer' value={fl150.employer} onChange={(e) => setFl150({ ...fl150, employer: e.target.value })} />
            <input className='rounded-lg border px-3 py-2' placeholder='Occupation' value={fl150.occupation} onChange={(e) => setFl150({ ...fl150, occupation: e.target.value })} />
            <input className='rounded-lg border px-3 py-2' placeholder='Business / LLC name (item 7)' value={fl150.business_name} onChange={(e) => setFl150({ ...fl150, business_name: e.target.value })} />
            <input className='rounded-lg border px-3 py-2' placeholder='Self-employment income (cents)' value={fl150.self_employment_income_cents} onChange={(e) => setFl150({ ...fl150, self_employment_income_cents: e.target.value })} />
          </div>
          <button
            type='button'
            className='rounded-xl bg-primary px-4 py-2 font-semibold text-white'
            onClick={async () => {
              const role = caseData.seats.find((s) => s.disclosure_answers !== undefined)?.role || 'petitioner';
              await apiFetch(getToken, `/v1/divorce/cases/${caseData.public_id}/seats/${role}/disclosure`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  employer: fl150.employer || undefined,
                  occupation: fl150.occupation || undefined,
                  business_name: fl150.business_name || undefined,
                  self_employment_income_cents: fl150.self_employment_income_cents
                    ? Number(fl150.self_employment_income_cents)
                    : undefined,
                }),
              });
              track('FL-150 Answers Saved', { stage: caseData.stage });
              await load(caseData.public_id);
            }}
          >
            Save my FL-150
          </button>
          <div className='flex flex-wrap gap-3'>
            {caseData.seats.map((seat) => (
              <button
                key={seat.role}
                type='button'
                className='rounded-xl border border-gray-300 px-3 py-2 text-sm font-semibold'
                onClick={async () => {
                  await apiFetch(getToken, `/v1/divorce/cases/${caseData.public_id}/seats/${seat.role}/fl141`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ attested: true, filed_on: new Date().toISOString().slice(0, 10) }),
                  });
                  track('FL-141 Attested', { role: seat.role });
                  await load(caseData.public_id);
                }}
              >
                {seat.fl141_attested ? `FL-141 on file (${seat.role})` : `Mark ${seat.role} FL-141 filed`}
              </button>
            ))}
          </div>
        </section>
        ) : (
          <section className='rounded-2xl border border-gray-200 p-5'>
            <h2 className='text-xl font-bold'>Your FL-150 is private</h2>
            <p className='mt-2 text-sm text-gray-600'>
              Claim your seat with a verified email to open your income and expense answers.
            </p>
          </section>
        )}

        <section className='space-y-3 rounded-2xl border border-gray-200 p-5'>
          <h2 className='text-xl font-bold'>If the clerk bounced the packet</h2>
          <p className='text-sm text-gray-600'>{REJECTION_AND_REVISION}</p>
          <label className='flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-gray-300 px-3 py-3 font-semibold text-gray-800'>
            <FiCamera /> Photograph the rejection notice
            <input
              type='file'
              accept='image/*'
              capture='environment'
              className='hidden'
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) {
                  track('Rejection Notice Uploaded');
                  void onFile(file, 'rejection_notice');
                }
              }}
            />
          </label>
          <button
            type='button'
            className='text-sm font-semibold text-primary'
            onClick={async () => {
              await apiFetch(getToken, `/v1/divorce/cases/${caseData.public_id}/reject`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ reasons: ['clerk rejection'], reopen_stage: 'judgment' }),
              });
              track('Judgment Stage Reopened');
              await load(caseData.public_id);
            }}
          >
            Reopen the judgment stage
          </button>
          <label className='flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-primary px-3 py-3 font-semibold text-primary'>
            <FiCamera /> Snap received FL-190
            <input
              type='file'
              accept='image/*'
              capture='environment'
              className='hidden'
              onChange={async (event) => {
                const file = event.target.files?.[0];
                if (!file) return;
                const result = await onFile(file, 'notice_of_entry_fl190');
                const extracted = result?.proposals?.find(
                  (proposal: { answer_path: string; value: unknown }) =>
                    proposal.answer_path === 'case.final_at',
                );
                if (extracted?.value) setFinalDate(String(extracted.value));
                track('Notice of Entry Uploaded');
              }}
            />
          </label>
          <div className='flex gap-2'>
            <input
              className='flex-1 rounded-lg border px-3 py-2'
              type='date'
              aria-label='Date judgment became final'
              value={finalDate}
              onChange={(event) => setFinalDate(event.target.value)}
            />
            <button
              type='button'
              disabled={!finalDate}
              className='rounded-xl bg-primary px-4 py-2 font-semibold text-white disabled:opacity-50'
              onClick={async () => {
                const response = await apiFetch(
                  getToken,
                  `/v1/divorce/cases/${caseData.public_id}/final`,
                  {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ final_on: finalDate }),
                  },
                );
                if (!response.ok) {
                  setError('Could not mark the case final.');
                  return;
                }
                track('Final Judgment Date Confirmed');
                await load(caseData.public_id);
              }}
            >
              Confirm final date
            </button>
          </div>
        </section>

        {caseData.form_watches.length ? (
          <p className='text-sm text-gray-600'>{caseData.form_watches[0].message}</p>
        ) : null}
        {error ? <p className='text-sm text-red-600'>{error}</p> : null}
        <p className='text-xs text-gray-500'>{DOC_PREP_DISCLAIMER}</p>
        {redactFile ? (
          <RedactModal
            file={redactFile}
            onCancel={() => setRedactFile(null)}
            onDone={async (file) => {
              setRedactFile(null);
              await ingestStatement(file);
            }}
          />
        ) : null}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function InviteResult({
  result,
  label,
}: {
  result: { message: string; url?: string };
  label: string;
}) {
  return (
    <div className='rounded-lg bg-primary/10 px-3 py-2 text-sm text-gray-700'>
      <span>{result.message}</span>
      {result.url ? (
        <button
          type='button'
          className='ml-2 font-semibold text-primary'
          onClick={async () => {
            await navigator.clipboard.writeText(result.url || '');
            track('Divorce Invite Link Copied', { invite_type: label });
          }}
        >
          Copy link
        </button>
      ) : null}
    </div>
  );
}

function RedactModal({
  file,
  onCancel,
  onDone,
}: {
  file: File;
  onCancel: () => void;
  onDone: (file: File) => void;
}) {
  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4'>
      <div className='max-w-lg rounded-2xl bg-white p-5'>
        <h3 className='text-lg font-bold'>Black out account numbers before upload</h3>
        <p className='mt-2 text-sm text-gray-600'>
          Paint over SSNs and full account numbers. Harbor never stores an SSN. The image is
          downscaled on your device.
        </p>
        <RedactCanvas
          file={file}
          onCancel={onCancel}
          onDone={onDone}
        />
      </div>
    </div>
  );
}

function RedactCanvas({
  file,
  onCancel,
  onDone,
}: {
  file: File;
  onCancel: () => void;
  onDone: (file: File) => void;
}) {
  const [ready, setReady] = useState(false);
  return (
    <div className='mt-4'>
      <canvas
        id='harbor-redact'
        className='max-h-80 w-full cursor-crosshair rounded-lg border'
        ref={(canvas) => {
          if (!canvas || ready) return;
          const ctx = canvas.getContext('2d');
          if (!ctx) return;
          const img = new window.Image();
          const url = URL.createObjectURL(file);
          img.onload = () => {
            URL.revokeObjectURL(url);
            const scale = Math.min(1, 1200 / Math.max(img.naturalWidth, img.naturalHeight));
            canvas.width = Math.round(img.naturalWidth * scale);
            canvas.height = Math.round(img.naturalHeight * scale);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            setReady(true);
            let drawing = false;
            canvas.style.touchAction = 'none';
            canvas.onpointerdown = (event) => {
              drawing = true;
              canvas.setPointerCapture(event.pointerId);
            };
            canvas.onpointerup = (event) => {
              drawing = false;
              canvas.releasePointerCapture(event.pointerId);
            };
            canvas.onpointercancel = () => {
              drawing = false;
            };
            canvas.onpointermove = (event) => {
              if (!drawing) return;
              const rect = canvas.getBoundingClientRect();
              const x = ((event.clientX - rect.left) / rect.width) * canvas.width;
              const y = ((event.clientY - rect.top) / rect.height) * canvas.height;
              ctx.fillStyle = '#111827';
              ctx.fillRect(x - 8, y - 8, 16, 16);
            };
          };
          img.src = url;
        }}
      />
      <div className='mt-4 flex gap-3'>
        <button type='button' className='font-semibold text-gray-600' onClick={onCancel}>
          Cancel
        </button>
        <button
          type='button'
          className='rounded-xl bg-primary px-4 py-2 font-semibold text-white'
          onClick={() => {
            const canvas = document.getElementById('harbor-redact') as HTMLCanvasElement | null;
            if (!canvas) return;
            canvas.toBlob(
              (blob) => {
                if (!blob) return;
                onDone(new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' }));
              },
              'image/jpeg',
              0.85,
            );
          }}
        >
          Upload redacted photo
        </button>
      </div>
    </div>
  );
}

function fileToJpegBase64(file: File, maxEdge = 1600): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let w = img.naturalWidth;
      let h = img.naturalHeight;
      const scale = Math.min(1, maxEdge / Math.max(w, h));
      w = Math.round(w * scale);
      h = Math.round(h * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('No canvas'));
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
      resolve(dataUrl.split(',')[1] || '');
    };
    img.onerror = () => reject(new Error('Could not read image'));
    img.src = url;
  });
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve(result.includes(',') ? result.split(',')[1] || '' : result);
    };
    reader.onerror = () => reject(reader.error || new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}
